import { IconName, IconNames } from '@blueprintjs/icons';
import semver from 'semver';

import {
  DataSourceType,
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import apacheDerby from 'src/templates/images/dataSources/apacheDerby.svg';
import apacheHive from 'src/templates/images/dataSources/apacheHive.svg';
import apacheImpala from 'src/templates/images/dataSources/apacheImpala.svg';
import awsAthena from 'src/templates/images/dataSources/awsAthena.svg';
import awsRedshift from 'src/templates/images/dataSources/awsRedshift.svg';
import azureSynapse from 'src/templates/images/dataSources/azureSynapse.svg';
import bigQuery from 'src/templates/images/dataSources/bigQuery.svg';
import cassandra from 'src/templates/images/dataSources/cassandra.svg';
import cosmosdb from 'src/templates/images/dataSources/cosmosdb.svg';
import databricks from 'src/templates/images/dataSources/databricks.svg';
import elasticsearch from 'src/templates/images/dataSources/elasticsearch.svg';
import exasol from 'src/templates/images/dataSources/exasol.svg';
import googleSheets from 'src/templates/images/dataSources/googleSheets.svg';
import h2 from 'src/templates/images/dataSources/h2.svg';
import ibmDb2 from 'src/templates/images/dataSources/ibmDb2.svg';
import jira from 'src/templates/images/dataSources/jira.svg';
import ldap from 'src/templates/images/dataSources/ldap.svg';
import mongodb from 'src/templates/images/dataSources/mongodb.svg';
import msSql from 'src/templates/images/dataSources/msSql.svg';
import mySql from 'src/templates/images/dataSources/mySql.svg';
import oracle from 'src/templates/images/dataSources/oracle.svg';
import postgresql from 'src/templates/images/dataSources/postgresql.svg';
import rest from 'src/templates/images/dataSources/rest.svg';
import salesforce from 'src/templates/images/dataSources/salesforce.svg';
import sapHana from 'src/templates/images/dataSources/sapHana.svg';
import snowflake from 'src/templates/images/dataSources/snowflake.svg';
import spark from 'src/templates/images/dataSources/spark.svg';
import splunk from 'src/templates/images/dataSources/splunk.svg';
import stardog from 'src/templates/images/dataSources/stardog.svg';
import sybaseAse from 'src/templates/images/dataSources/sybaseAse.svg';
import teradata from 'src/templates/images/dataSources/teradata.svg';
import trino from 'src/templates/images/dataSources/trino.svg';

export const icons = {
  connection: IconNames.OFFLINE,
  database: IconNames.DATABASE,
  fileUpload: IconNames.DOCUMENT,
  model: IconNames.DIAGRAM_TREE,
  project: IconNames.PROJECTS,
  modelUpload: IconNames.UPLOAD,
  music: IconNames.MUSIC,
  table: IconNames.TH,
};

const baseDataSourceIcons = {
  // Icons for 8.0.0
  ATHENA: [awsAthena],
  BIGQUERY: [bigQuery],
  CASSANDRA: [cassandra],
  DB2: [ibmDb2],
  DERBY: [apacheDerby],
  ELASTICSEARCH: [elasticsearch],
  EXASOL: [exasol],
  GOOGLESHEETS: [googleSheets],
  H2: [h2],
  HANA: [sapHana],
  HIVE: [apacheHive],
  IMPALA: [apacheImpala],
  JIRA: [jira],
  LDAP_CDATA: [ldap],
  MONGODB: [mongodb, cosmosdb],
  MSSQL: [msSql],
  MYSQL: [mySql],
  ORACLE: [oracle],
  POSTGRESQL: [postgresql],
  REDSHIFT: [awsRedshift],
  REST: [rest],
  SALESFORCE_CDATA: [salesforce],
  SALESFORCE_SIMBA: [salesforce],
  SNOWFLAKE: [snowflake],
  SPARKSQL: [databricks, spark],
  SPARQL: [stardog],
  SPLUNK: [splunk],
  SYBASE: [sybaseAse],
  TERADATA: [teradata],
};
const dataSourceIconsv810 = {
  // Icons for 8.1.0
  ...baseDataSourceIcons,
  SPARKSQL_DATABRICKS: [databricks],
  SPARKSQL: [spark],
};
const dataSourceIconsv900 = {
  // Icons for 9.0.0
  ...dataSourceIconsv810,
  SYNAPSE_DEDICATED: [azureSynapse],
  SYNAPSE_SERVERLESS: [azureSynapse],
};

const dataSourceIconsv1000 = {
  // Icons for 10.0.0
  ...dataSourceIconsv900,
  TRINO: [trino],
};

const dataSourceIcons: Record<string, Record<DataSourceType, string[]>> = {
  '0.0.0': baseDataSourceIcons,
  '8.1.0': dataSourceIconsv810,
  '9.0.0': dataSourceIconsv900,
  '10.0.0': dataSourceIconsv1000,
};
// As new icons are added, this should be updated to avoid blank nodes in the
// canvas (as Stardog version is not yet stored / available in Diagram state)
// See also: https://stardog-union.slack.com/archives/GF5TQNJLX/p1689118076639889
const defaultDataSourceIconVersion = '9.0.0';

type DataSourceIcon =
  | {
      icon: string;
      isBlueprintIcon: false;
      isCombinedIcon: boolean;
    }
  | {
      icon: IconName;
      isBlueprintIcon: true;
      isCombinedIcon: false;
    };

export const getDataSourceIcon = ({
  dataSourceType,
  stardogVersion,
  enforceSquareDimensions,
}: {
  dataSourceType: DataSourceType;
  stardogVersion?: string;
  enforceSquareDimensions?: boolean;
}): DataSourceIcon => {
  if (dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE) {
    return {
      icon: icons.music,
      isBlueprintIcon: true,
      isCombinedIcon: false,
    };
  }
  const maxVersion =
    stardogVersion === undefined
      ? defaultDataSourceIconVersion
      : semver.maxSatisfying(
          Object.keys(dataSourceIcons),
          `<=${stardogVersion}`
        ) || defaultDataSourceIconVersion;
  const iconLookup = dataSourceIcons[maxVersion];
  if (iconLookup?.[dataSourceType]) {
    let icon = iconLookup[dataSourceType][0];
    let isCombinedIcon = false;
    if (iconLookup[dataSourceType].length === 2) {
      isCombinedIcon = true;
      // go.js requires that both width and height be specified instead of
      // relying on CSS preserve the aspect ratio
      if (enforceSquareDimensions) {
        // eslint-disable-next-line deprecation/deprecation
        icon = `data:image/svg+xml;base64,${btoa(`
        <svg width="400" height="400" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
          <image x="20" y="125" width="150" height="150" href="${iconLookup[dataSourceType][0]}"/>
          <image x="230" y="125" width="150" height="150" href="${iconLookup[dataSourceType][1]}"/>
          <line x1="200" x2="200" y1="80" y2="320" stroke="#8A9BA8" stroke-width="3" />
        </svg>`)}`;
      } else {
        // eslint-disable-next-line deprecation/deprecation
        icon = `data:image/svg+xml;base64,${btoa(`
        <svg width="400" height="150" viewBox="0 0 400 150" fill="none" xmlns="http://www.w3.org/2000/svg">
          <image x="0" y="0" width="150" height="150" href="${iconLookup[dataSourceType][0]}"/>
          <image x="250" y="0" width="150" height="150" href="${iconLookup[dataSourceType][1]}"/>
          <line x1="200" x2="200" y1="0" y2="150" stroke="#8A9BA8" stroke-width="3" />
        </svg>`)}`;
      }
    }
    return {
      icon,
      isBlueprintIcon: false,
      isCombinedIcon,
    };
  }
  return {
    icon: icons.table,
    isBlueprintIcon: true,
    isCombinedIcon: false,
  };
};
